"use client";

import { ExternalLink, RefreshCw } from "lucide-react";

import { FileType } from "@/components/utils/uploadFile";
import LomdaUploadFile from "../shared/lomdaUploadFile";
import { Input } from "@/components/ui/input";
import { t } from "@/lib/localization";
import { TextBlock } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import {
    LOMDA_DROPZONE_CLASS,
    LOMDA_DROPZONE_DRAG_CLASS,
} from "../shared/dropzone";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

export default function LinkVariant({ block }: { block: TextBlock }) {
    const locale = useLomdaLocale();
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const editable = useBlockEditable();

    const data = block.data;
    if (data.variant !== "link") return null;

    if (!editable) {
        return (
            <a
                href={data.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-stretch gap-4 overflow-hidden rounded-xl border transition hover:border-primary/50"
            >
                {data.thumbnail && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={data.thumbnail}
                        alt={data.title}
                        className="h-24 w-24 shrink-0 object-cover sm:w-32"
                    />
                )}
                <div className="flex flex-col justify-center gap-1 py-3 pe-4">
                    <span className="font-semibold">{data.title}</span>
                    <span className="lomda-v3-text-small text-muted-foreground">
                        {data.description}
                    </span>
                    <span className="inline-flex items-center gap-1 lomda-v3-text-small text-primary">
                        <ExternalLink className="size-3.5" />
                        {t("lomda-v3-editor.link.visit", locale)}
                    </span>
                </div>
            </a>
        );
    }

    return (
        <div className="flex items-stretch gap-4 overflow-hidden rounded-xl border p-3">
            <div className="w-32 shrink-0">
                {data.thumbnail ? (
                    <div className="group/photo relative h-24 w-full overflow-hidden rounded-md">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={data.thumbnail}
                            alt={data.title}
                            className="h-full w-full object-cover"
                        />
                        <button
                            type="button"
                            title={t("lomda-v3-editor.link.replace-thumbnail")}
                            onClick={() => updateBlock(block.id, { thumbnail: "" })}
                            className="absolute inset-0 flex items-center justify-center bg-foreground/50 text-background opacity-0 transition group-hover/photo:opacity-100"
                        >
                            <RefreshCw className="size-4" />
                        </button>
                    </div>
                ) : (
                    <LomdaUploadFile
                        record={false}
                        reuse="image"
                        reuseLayout="compact"
                        options={{
                            allowedFileTypes: [FileType.Images],
                            maxFileSize: 5,
                            className:
                                `${LOMDA_DROPZONE_CLASS} h-24 w-full rounded-md`,
                            dragClassName: LOMDA_DROPZONE_DRAG_CLASS,
                            showSelectedFileName: false,
                            title: {
                                text: t("lomda-v3-editor.link.thumbnail"),
                                keyName: "lomda-v3-editor.link.thumbnail",
                            },
                            subtitle: { text: "", keyName: "lomda-v3-editor.empty" },
                        }}
                        onUploadFinished={(file) =>
                            updateBlock(block.id, { thumbnail: file.fileUrl })
                        }
                    />
                )}
            </div>

            <div className="flex flex-1 flex-col justify-center gap-1.5">
                <Input
                    value={data.title}
                    onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                    placeholder={t("lomda-v3-editor.link.title")}
                    className="h-8 font-semibold"
                />
                <Input
                    value={data.description}
                    onChange={(e) =>
                        updateBlock(block.id, { description: e.target.value })
                    }
                    placeholder={t("lomda-v3-editor.link.description")}
                    className="h-8 lomda-v3-text-small"
                />
                <div className="flex items-center gap-1.5">
                    <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />
                    <Input
                        value={data.url}
                        onChange={(e) => updateBlock(block.id, { url: e.target.value })}
                        placeholder={t("lomda-v3-editor.link.url")}
                        dir="ltr"
                        className="h-8 lomda-v3-text-small"
                    />
                </div>
            </div>
        </div>
    );
}
